// @ts-strict-ignore
import { ACCOUNT_LEVEL_CAPABILITIES } from 'constants/capabilities'
import {
  BulkCapabilityRequest,
  BulkCapabilityRequestFilterObject,
  BulkCapabilityResponse,
  CapabilitiesScope,
} from 'types/capabilities/capabilities'
import { stringToArray } from './utils'

export const getWalletActionCapabilityRequestFilters = (walletLsid: string[]) => {
  if (!walletLsid?.length) return

  const walletFilters: BulkCapabilityRequestFilterObject[] = walletLsid?.map(walletLsid => {
    return {
      scope: CapabilitiesScope.WALLET,
      subjectId: walletLsid,
    }
  })

  return {
    capabilities: [
      {
        group: 'FIAT',
        name: 'WALLET_ACTIONS',
        objects: walletFilters,
      },
      {
        group: 'FIAT',
        name: 'PAYOUTS_B2C',
        objects: walletFilters,
      },
    ],
  } as BulkCapabilityRequest
}

export const getAccountCapabilityRequestFilters = (accountReference: string) => {
  if (!accountReference) return

  const accountFilters: BulkCapabilityRequestFilterObject[] = [
    {
      scope: CapabilitiesScope.ACCOUNT,
      subjectId: accountReference,
    },
  ]

  const accountCapabilityFilters = ACCOUNT_LEVEL_CAPABILITIES?.map(accountCapability => {
    return {
      ...accountCapability,
      objects: accountFilters,
    }
  })

  return {
    capabilities: accountCapabilityFilters,
  } as BulkCapabilityRequest
}

/**
 * Custom hook to return account capabilities from the capabilities management service
 *
 * @param {string} accountReference - The account reference to fetch capabilities on an account level
 * @param {BulkCapabilityResponse} bulkCapabilities - The bulk wallet capabilities response.
 * @returns {AccountCapabilities} - Returns the account capabilities or null if not yet fetched.
 */
export const getAccountCapabilities = (
  accountReference: string,
  bulkCapabilities: BulkCapabilityResponse
) => {
  // Underlying customer (nested and embedded)
  const isPrincipleCustomerCompanyCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.PRINCIPLE_CUSTOMER_COMPANY?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isPrincipleCustomerIndividualCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.PRINCIPLE_CUSTOMER_INDIVIDUAL?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isEmbeddedCustomerCompanyCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.EMBEDDED_CUSTOMER_COMPANY?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isEmbeddedCustomerIndividualCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.EMBEDDED_CUSTOMER_INDIVIDUAL?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isEarningRewardsCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.EARNING_REWARDS?.ACCOUNT?.[accountReference]?.value === 'true'

  // Crypto capabilities
  const paxosUsStatesCapability = stringToArray(
    bulkCapabilities?.CRYPTO?.US_STATES_CRYPTO_PAXOS_ENABLED?.ACCOUNT?.[accountReference]?.value
  )

  // Fiat beneficiaries
  const isCubixBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_CUBIX?.ACCOUNT?.[accountReference]?.value === 'true'

  const isAedBeneficiaryEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_AED?.ACCOUNT?.[accountReference]?.value === 'true'

  const isUsdCRNowBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_CRNOW?.ACCOUNT?.[accountReference]?.value === 'true'

  const isUsdRtpBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_RTP?.ACCOUNT?.[accountReference]?.value === 'true'

  const isUsdFedNowBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_FEDNOW?.ACCOUNT?.[accountReference]?.value === 'true'

  const isLatamBankAccountCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_LATAM?.ACCOUNT?.[accountReference]?.value === 'true'

  const isMcMoveBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_MC_MOVE?.ACCOUNT?.[accountReference]?.value === 'true'
  // EPC wallet actions
  const epcWalletActions = bulkCapabilities?.EPC?.WALLET_ACTIONS?.ACCOUNT?.[accountReference]?.value
    ?.toString()
    ?.split(',')
  const isEpcWalletPayinsCapabilityEnabled = epcWalletActions?.includes('payins')
  const isEpcWalletPayoutsCapabilityEnabled = epcWalletActions?.includes('payouts')
  const isEpcWalletInternalTransfersCapabilityEnabled =
    epcWalletActions?.includes('internal_transfers')

  // EPC embedded customer wallet creation (not nested)
  const isEpcEmbeddedCustomerWalletCreationCapabilityEnabled =
    bulkCapabilities?.EPC?.EMBEDDED_WALLET_CREATION?.ACCOUNT?.[accountReference]?.value === 'true'

  const isPartnerManagedCustomerCompanyCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.PARTNER_MANAGED_CUSTOMER_COMPANY?.ACCOUNT?.[accountReference]
      ?.value === 'true'
  const isPartnerManagedCustomerIndividualCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.PARTNER_MANAGED_CUSTOMER_INDIVIDUAL?.ACCOUNT?.[accountReference]
      ?.value === 'true'

  return {
    // Underlying customer (nested and embedded)
    isPrincipleCustomerCompanyCapabilityEnabled,
    isPrincipleCustomerIndividualCapabilityEnabled,
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
    isPartnerManagedCustomerCompanyCapabilityEnabled,
    isPartnerManagedCustomerIndividualCapabilityEnabled,
    isEarningRewardsCapabilityEnabled,
    // Crypto capabilities
    paxosUsStatesCapability,
    // Fiat beneficiaries
    isCubixBeneficiaryCapabilityEnabled,
    isAedBeneficiaryEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
    isLatamBankAccountCapabilityEnabled,
    isMcMoveBeneficiaryCapabilityEnabled,
    // EPC wallet actions
    isEpcWalletPayinsCapabilityEnabled,
    isEpcWalletPayoutsCapabilityEnabled,
    isEpcWalletInternalTransfersCapabilityEnabled,
    isEpcEmbeddedCustomerWalletCreationCapabilityEnabled,
  }
}
